import {AnyElement, AnyNode, create, ifjs, nullElement, signalMap, StringOrSignal} from "../../fjsc/src/f2.ts";
import {NotificationType} from "../enums/NotificationType.ts";
import {compute, signal, Signal} from "../../fjsc/src/signals.ts";
import {Nav} from "./nav.ts";
import {Route} from "../routing/Route.ts";
import type {NavItem} from "../models/NavItem.ts";
import {Albums} from "./albums.ts";
import {Tracks} from "./tracks.ts";
import {Tab} from "../models/Tab.ts";
import {FJSC} from "../../fjsc";
import {navigate} from "../routing/Router.ts";
import {Home} from "./home.ts";
import {Submission} from "./submission.ts";
import {dayFrom} from "../functions/dates.ts";
import {Time} from "../functions/Time.ts";
import {currency} from "../functions/formatters.ts";
import {Artists} from "./artists.ts";
import {About} from "./about.ts";

export class Generics {
    static notFound() {
        return Generics.pageFrame(
            create("h1")
                .text("404")
                .build()
        );
    }

    static nav() {
        return create("nav")
            .classes("flex", "center-items")
            .children(
                ...routes.filter(r => r.showInNav === true)
                    .map(r => {
                        return Nav.navItem(<NavItem>{
                            text: r.title,
                            path: r.path,
                            icon: r.icon,
                        });
                    })
            ).build();
    }

    static pageFrame(...content: (AnyElement | Signal<AnyElement>)[]) {
        return create("div")
            .classes("container", "flex-v", "fill-parent")
            .children(
                Generics.nav(),
                ...content
            ).build();
    }

    static container(layer: number, content: (AnyElement | Signal<AnyElement>)[], extraClasses: string[] = []) {
        return create("div")
            .classes("container", "border", "layer-" + layer, ...extraClasses)
            .children(...content)
            .build();
    }

    static message(message: StringOrSignal) {
        return ifjs(message, create("span")
            .text(message)
            .build());
    }

    static image(src: StringOrSignal, extraClasses: StringOrSignal[] = [], size = "50") {
        return create("img")
            .classes(...extraClasses)
            .height(size)
            .src(src)
            .build();
    }

    static icon(icon: StringOrSignal, classes: StringOrSignal[] = [], onclick: Function = () => {
    }) {
        return create("i")
            .classes("material-symbols-outlined", ...classes)
            .text(icon)
            .onclick(onclick)
            .build();
    }

    static iconButton(icon: StringOrSignal, classes: StringOrSignal[] = [], onclick: Function = () => {
    }) {
        return create("div")
            .classes("icon-button", ...classes)
            .children(
                Generics.icon(icon, [], onclick)
            ).build();
    }

    static notification(type: NotificationType = NotificationType.success, text = "Success!") {
        return create("div")
            .classes("notification", "container", "border", type)
            .text(text)
            .build();
    }

    static list<T>(entries: Signal<T[]> | T[], template: (entry: T) => AnyElement) {
        if (entries instanceof Signal) {
            return create("div")
                .classes("container", "layer-2")
                .children(
                    signalMap(entries, create("div").classes("flex-v"), template)
                ).build();
        }

        return create("div")
            .classes("container", "layer-2", "flex-v")
            .children(
                ...entries.map(template)
            ).build();
    }

    static loading() {
        return create("div")
            .classes("loading")
            .build();
    }

    static heading(level: number, text: StringOrSignal, classes: StringOrSignal[] = []) {
        return create(`h${level}`)
            .classes(...classes)
            .text(text)
            .build();
    }

    static table<T>(headers: StringOrSignal[], entries: Signal<T[]> | T[], rowTemplate: (entry: T) => AnyElement) {
        if (entries instanceof Signal) {
            return create("table")
                .classes("container", "layer-2")
                .children(
                    create("thead")
                        .children(
                            create("tr")
                                .children(
                                    ...headers.map(c => create("th").classes(`header-${(c as string).replaceAll(/\s/g, '-').toLowerCase()}`).text(c).build())
                                ).build()
                        ).build(),
                    signalMap(entries, create("tbody"), rowTemplate)
                ).build();
        }

        return create("table")
            .classes("container", "layer-2")
            .children(
                create("thead")
                    .children(
                        create("tr")
                            .children(
                                ...headers.map(c => create("th").text(c).build())
                            ).build()
                    ).build(),
                create("tbody")
                    .children(
                        ...entries.map(rowTemplate)
                    ).build()
            ).build();
    }

    static tableRow(...data: any[]) {
        return create("tr")
            .children(
                ...data.map(d => create("td").text(d).build())
            ).build();
    }

    static tabSelector(tab$: Signal<string>, tabs: Tab[]) {
        return create("div")
            .classes("flex", "center-items")
            .children(
                ...tabs.map(tab => {
                    const activeClass = compute((t): string => t === tab.key ? "active" : "_", tab$);

                    return FJSC.button({
                        text: tab.text,
                        icon: {icon: tab.icon},
                        classes: [activeClass],
                        onclick: () => {
                            tab$.value = tab.key;
                        }
                    })
                })
            ).build();
    }

    static tabContents(tab$: Signal<string>, templateMap: Record<string, Function>) {
        const template = compute(t => {
            if (templateMap[t]) {
                return templateMap[t]();
            }
            return nullElement();
        }, tab$);

        return create("div")
            .children(template)
            .build();
    }

    static link(url: StringOrSignal, title: StringOrSignal, classes: StringOrSignal[] = []) {
        let isRemote = false;
        if (typeof url === "string") {
            isRemote = url.includes("http");
        } else {
            isRemote = url.value.includes("http");
        }

        return create("div")
            .classes("link-container")
            .children(
                create("a")
                    .classes("underline", ...classes)
                    .href(url)
                    .target(isRemote ? "_blank" : "_self")
                    .title(url)
                    .text(title)
                    .onclick(e => {
                        if (!isRemote && e.button === 0) {
                            e.preventDefault();
                            if (typeof url === "string") {
                                navigate(url);
                            } else {
                                navigate(url.value);
                            }
                        }
                    }).build()
            ).build();
    }

    static divider() {
        return create("hr")
            .build();
    }

    static date(date: Signal<string>) {
        const dateAsDay = compute(d => dayFrom(d), date);
        const ago = compute(d => Time.ago(new Date(d)), date);

        return Generics.partedContainer([dateAsDay, ago]);
    }

    static partedContainer(strings: StringOrSignal[]) {
        return create("div")
            .classes("parted-container")
            .children(
                ...strings.map(s => create("span")
                    .text(s)
                    .build())
            ).build();
    }

    static currency(amount: Signal<number>) {
        const formatted = compute(a => currency(a), amount);

        return create("span")
            .classes("currency")
            .text(formatted)
            .build();
    }

    static paragraph(text: StringOrSignal) {
        return create("p")
            .text(text)
            .build();
    }
}

export const routes: Route[] = [
    {
        path: "/",
        title: "Home",
        aliases: ["home"],
        template: Home.page,
        icon: "home",
        showInNav: true,
    },
    {
        path: "404",
        title: "404",
        aliases: ["error", "not-found"],
        template: Generics.notFound,
    },
    {
        path: "releases",
        title: "Releases",
        template: Albums.page,
        icon: "album",
        showInNav: true,
    },
    {
        path: "artists",
        title: "Artists",
        template: Artists.page,
        icon: "group",
        showInNav: true,
    },
    {
        path: "submit",
        title: "Submit",
        template: Submission.page,
        icon: "move_to_inbox",
        showInNav: true,
    },
    {
        path: "album",
        aliases: ["release"],
        pathParams: ["id"],
        title: "Album",
        template: Albums.albumPage,
        icon: "album",
    },
    {
        path: "artist",
        pathParams: ["name"],
        title: "Artist",
        template: Artists.artistPage,
        icon: "artist",
    },
    {
        path: "about",
        title: "About",
        template: About.page,
        icon: "info",
        showInNav: true,
    },
    {
        path: "track",
        pathParams: ["id"],
        title: "Track",
        template: Tracks.trackPage,
        icon: "album",
    },
    {
        path: "submission-success",
        title: "Submission success",
        template: Submission.successPage,
        icon: "check",
    }
];